/**
 * ویرایشِ یک پیش‌نویس — کوچک‌تر از `SourceEditor` و فوری‌تر.
 *
 * ═══ چرا جدا از `SourceEditor` ═══
 *
 * دو تفاوتِ ریشه‌ای دارند و یکی کردنشان یعنی یک فرم با نصفِ فیلدهای
 * خاموش و یک پرچمِ «این یکی صف دارد آن یکی ندارد»:
 *
 *   • **این یکی همان لحظه می‌نشیند.** `source_drafts` هیچ‌کدام از سه
 *     نامتغیرِ رجیستری را ندارد، پس نوشتنش از SQL بی‌خطر است — همان
 *     استدلالِ `addDraft`.
 *   • **فیلدهایش کمترند و باید کم بمانند.** `tier`، `trust`، `enabled`
 *     روی پیش‌نویس معنی ندارند: هنوز چیزی اجرا نمی‌شود. موقعِ ترفیع پیدا
 *     می‌شوند.
 *
 * ═══ چرا اصلاً لازم شد ═══
 *
 * مدل آدرس را حدس می‌زند و گاهی غلط می‌زند (`/events` به‌جای
 * `/whats-on`). تا امروز تنها کارِ ممکن این بود که منتظرِ ❌ بمانی و بعد
 * یک پیش‌نویسِ دستیِ تازه بسازی — با اینکه فرقِ آن دو یک رشته بود.
 */
import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { sourceRepo } from '@/core/data/sourceRepo';
import {
  changedFields, EXTRACTOR_CHOICES, TIMEZONE_CHOICES,
} from '@/core/lib/sourcePipeline';
import { reportError } from '@/core/lib/report';

const str = (v: unknown): string => (v == null ? '' : String(v));
const list = (v: unknown): string[] => (Array.isArray(v) ? (v as string[]) : []);

export function DraftEditor({ draftId, current, onDone }: {
  draftId: string;
  /** `payload`ِ ردیفِ پیش‌نویس — یک `Source` کامل. */
  current: Record<string, unknown>;
  onDone: (message: string) => void;
}) {
  const qc = useQueryClient();
  const [label, setLabel] = useState(str(current.label));
  const [extractor, setExtractor] = useState(str(current.extractor));
  const [timezone, setTimezone] = useState(str(current.timezone));
  const [urls, setUrls] = useState(list(current.urls).join('\n'));
  const [notes, setNotes] = useState(str(current.notes));
  const [error, setError] = useState<string | null>(null);

  const build = (): Record<string, unknown> => ({
    label: label.trim(),
    extractor: extractor.trim(),
    timezone: timezone.trim() || null,
    urls: urls.split('\n').map((u) => u.trim()).filter(Boolean),
    notes: notes.trim() || null,
  });

  const save = useMutation({
    mutationFn: () => {
      const patch = changedFields(current, build());
      if (Object.keys(patch).length === 0) {
        return Promise.reject(new Error('چیزی عوض نشده.'));
      }
      return sourceRepo.editDraft(draftId, patch).then(() => Object.keys(patch));
    },
    onSuccess: (fields) => {
      // ⚠️ هر سه: خودِ ردیف عوض شده، جزئیاتش عوض شده، و حکمِ ترفیعش پاک
      // شده — یعنی «مرحله»ی ردیف هم می‌تواند از «ردشده» به «پیش‌نویس»
      // برگردد. بی این، جدول یک چیز می‌گوید و دیتابیس چیزِ دیگری.
      qc.invalidateQueries({ queryKey: ['srcPipeline'] });
      qc.invalidateQueries({ queryKey: ['srcDetail'] });
      onDone(`پیش‌نویسِ ${draftId} به‌روز شد: ${fields.join('، ')}`);
    },
    onError: (err) => {
      reportError('admin.sources.editDraft', err);
      setError(err instanceof Error ? err.message : String(err));
    },
  });

  const dirty = Object.keys(changedFields(current, build()));

  return (
    <>
      <input className="input" value={label} placeholder="نامِ نمایشی"
        onChange={(e) => setLabel(e.target.value)} />

      {/* ⚠️ یک آدرس در هر خط، و **دامنه می‌تواند عوض شود**. جشنواره‌ها و
          سالن‌ها معمولاً تقویمشان را روی پلتفرمِ ثالث می‌گذارند
          (eventival، eventbrite، ticketmaster)، پس قفل کردنِ دامنه یعنی
          هیچ‌وقت نشود به جایی اشاره کرد که رخدادها واقعاً آن‌جا هستند.
          تنها گارد این است که دامنه را منبعِ دیگری در همین شهر برنداشته
          باشد — و آن خطا همین‌جا می‌آید، نه ماه‌ها بعد موقعِ ترفیع. */}
      <textarea className="input textarea" value={urls} dir="ltr"
        placeholder="یک آدرس در هر خط" onChange={(e) => setUrls(e.target.value)} />

      {/* ⚠️ دراپ‌داون و نه ورودیِ آزاد: نامی که کرالر نشناسد یک منبعِ
          خاموشِ بی‌خطا می‌سازد — `unsupported-extractor` شمرده می‌شود و
          هیچ‌جا نمی‌گوید چرا. `tests/test_extractor_choices_match.py` در
          مخزنِ کرالر همین فهرست را با `EXTRACTORS` مقابله می‌کند. */}
      <div className="filters" style={{ marginBottom: 8 }}>
        <label className="dim" htmlFor="dr-ex">extractor</label>
        <select id="dr-ex" value={extractor} onChange={(e) => setExtractor(e.target.value)}>
          {EXTRACTOR_CHOICES.map((x) => (
            <option key={x.value} value={x.value}>{x.label}</option>
          ))}
        </select>
      </div>

      {/* غلطِ املایی این‌جا یعنی رخدادهایی که چند ساعت جابه‌جا منتشر
          می‌شوند، بی‌آنکه چیزی خطا بدهد. */}
      <div className="filters" style={{ marginBottom: 8 }}>
        <label className="dim" htmlFor="dr-tz">منطقه‌ی زمانی</label>
        <select id="dr-tz" value={timezone} onChange={(e) => setTimezone(e.target.value)}>
          <option value="">— تعیین نشده —</option>
          {TIMEZONE_CHOICES.map((z) => <option key={z} value={z}>{z}</option>)}
        </select>
      </div>
      <textarea className="input textarea" value={notes} placeholder="یادداشت"
        onChange={(e) => setNotes(e.target.value)} />

      {/*
        ⚠️ مهم‌ترین جمله‌ی این فرم. مدل همیشه `jsonld` می‌گذارد — یک مقدارِ
        ثابت، نه حدسِ سایت‌به‌سایت. پس اگر تست صفر فیلد داد و آیتم‌ها را
        دیده، اولین چیزی که باید امتحان شود همین است.
      */}
      <p className="dim" style={{ fontSize: 12 }}>
        هر پیش‌نویس با <span className="mono">jsonld</span> ساخته می‌شود، چون
        مدل همیشه همین را می‌گذارد. اگر تست آیتم پیدا کرد ولی فیلدی نداد،
        <span className="mono"> cards</span> را امتحان کن.
      </p>

      {error ? <p className="err">{error}</p> : null}

      <p className="dim" style={{ marginBottom: 6 }}>
        {dirty.length === 0 ? 'چیزی عوض نشده.' : `عوض می‌شود: ${dirty.join('، ')}`}
      </p>

      {dirty.length > 0 ? (
        <p className="note">
          همان لحظه ثبت می‌شود (پیش‌نویس صف ندارد). حکمِ ترفیعِ این ردیف هم
          پاک می‌شود تا دوباره سنجیده شود — وگرنه ردیفی که آدرسش را درست
          کرده‌ای تا ابد «ردشده» می‌ماند.
          {dirty.includes('urls') ? (
            <> دامنه هم با آدرسِ اول هماهنگ می‌شود، و آدرسِ تکراری حذف.</>
          ) : null}
        </p>
      ) : null}

      <button className="btn block" type="button"
        disabled={dirty.length === 0 || save.isPending}
        onClick={() => { setError(null); save.mutate(); }}>
        {save.isPending ? '…' : 'ثبت'}
      </button>
    </>
  );
}
